import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { navigate, useRoute } from '../lib/router';
import { useRemembered } from '../lib/remember';
import { useNotice } from '../shell/notice';
import { useFlag, useMatches } from '../shell/hooks';
import { ChipRow, FilterChip, InterestDots } from '../ui/bits';
import { ListRow, ListTable, type Col } from '../ui/ListTable';
import { INTEREST_HINTS, interestHint } from '../book/changes';
import { bookLabel, todayIn } from '../book/model';
import { loadDetails, loadFilterFacts, loadReferenceHeads, type Details, type Shelves } from '../shelves/load';
import { buildGroups, filterCount, matches, pruneSubgenres, recentFrom, type FilterFacts, type FilterLists, type FilterState, type Group } from '../shelves/filters';
import { FilterBar, FilterPanel } from '../shelves/Filters';
import type { ShelfBook } from '../shelves/model';
import { BookSheet, type BookSheetKind } from './BookSheet';
import { BulkBar, InterestMenu, MoveMenu, NARROW_INTEREST, RowBook, RowCheck, SelectShown, TypeMenu, flipSort } from './parts';
import { PillMenu, SettingMenu } from '../ui/PillMenu';
import { setBooksPaperState } from '../mybooks/actions';
import { ListFoot } from '../ui/ListFoot';
import { Segmented } from '../ui/Segmented';
import { BackLink } from '../ui/BackLink';
import { setBook } from './actions';
import { PAGE, copyText, num, rowsOf, type QueueRow } from './model';
import { myBooksTab } from '../mybooks/model';
import { RECENT_SORTS, addedText, recentLine, cameFromChips, cameFromOf, everyBook, howAdded, recentRows, sortRecent, statusText, type RecentSort, type RecentSortKey, type When } from './recent';
import { useAmAdmin } from '../lib/admin';
import { loadRecentFacts, type Batch } from './recentLoad';
import { type Suggestion } from './review';
import { loadSuggestions } from './reviewLoad';
import { lookupItems, type Dismissal, type LookupRow } from './lookups';
import { loadDismissals, loadLookups } from './lookupLoad';
import { ReviewView } from './ReviewView';

// My books › Recently added (moved from the Queue, Phase 6.4 Part 1, Kristi Oct 31; built as the Queue's Phase 6.3 Part 3, agreed with Kristi Oct 30; mocked up in chat):
// one labelled Showing menu (Kristi, Oct 30): added by you lately · everything added lately,
// imports included · one import's books, any date; No shelf yet narrows whichever is showing. Columns: Added · Book · How added ·
// Status · Read type · Interest · ★ · Copy · Move ▾ (★ and Copy fold under
// the title up to 1,000 px). Review (Part 4) is its own tab.


/** Recently added's Filters rows, in this order. */
const RECENT_FILTERS = ['came_from', 'copies', 'format', 'shelf'] as const;
/** Update details › No shelf yet's rows (F13, Kristi Nov 2): Format · Recently added · Came from. */
const NO_SHELF_FILTERS = ['came_from', 'format', 'recent'] as const;

/**
 * Where Recently added's table is drawn (filters review, Kristi Nov 2): as the last section of
 * Add & import ('section'), or as My books › Update details, whose first list (No shelf yet) is the
 * same table showing only books with no shelf yet ('details').
 */
export type RecentView = 'section' | 'details';

/** "See these books ›" on a past import (F11): that import only, any date, with your filters kept to go back to. */
export const RECENT_KEPT = 'my_books:recent:kept';
export const DETAILS_FILTERS = 'my_books:details:filters';
export const DETAILS_KEPT = 'my_books:details:kept';

export function RecentList({
  wide,
  shelves,
  readTypes,
  onCount,
  view: mode,
  top,
  headSlot,
  hideBack,
}: {
  wide: boolean;
  shelves: Shelves;
  readTypes: string[];
  onCount?: (n: number) => void;
  view: RecentView;
  top?: ComponentChildren;
  /** Add & import's heading slot: When sits at the right of the section's heading. */
  headSlot?: HTMLElement | null;
  /** Another back link is showing (Add & import's "← Back to your import"): one back link at a time (R8). */
  hideBack?: boolean;
}) {
  const reviewing = mode === 'details';
  const notice = useNotice();
  const today = todayIn();
  const { path } = useRoute();
  const narrowInterest = useMatches(NARROW_INTEREST);
  const editOn = useFlag('book_page.edit');
  // Still kept under their Queue names, so this device's choices came along with the move.
  // When (Kristi, Oct 31): the last N days or any date; where from is Filters › Came from.
  const [when, setWhen] = useRemembered<When>('queue:recent:when', 'recent');
  const admin = useAmAdmin();
  const [noStatus, setNoStatus] = useRemembered<boolean>('queue:recent:nostatus', false);
  const [sort, setSort] = useRemembered<RecentSort>('queue:sort:recent', { key: 'added', reversed: false });
  // Update details' No shelf yet list keeps its own filters (filters stick per view, F11).
  const [savedFilters, setSavedFilters] = useRemembered<FilterState>(reviewing ? DETAILS_FILTERS : 'queue:filters:recent', {});
  // Arriving from a link (F11): the filters that were on before, for "← Back to your filters".
  const [kept, setKept] = useRemembered<{ filters: FilterState; when?: When; noStatus?: boolean } | null>(reviewing ? DETAILS_KEPT : RECENT_KEPT, null);
  const rowKeys: readonly string[] = reviewing ? NO_SHELF_FILTERS : RECENT_FILTERS;
  // Only the filters that help with what was just added (Kristi, Oct 31): Came from, Copies, Format, Shelf.
  // Anything else saved earlier on this device is ignored, so it can't narrow the list unseen.
  const filters = useMemo(() => {
    const o: FilterState = {};
    for (const k of rowKeys) if (savedFilters[k as keyof FilterState]) o[k as keyof FilterState] = savedFilters[k as keyof FilterState];
    return o;
  }, [savedFilters, reviewing]);
  const backToFilters = () => {
    if (!kept) return;
    setSavedFilters(kept.filters);
    if (!reviewing && kept.when) setWhen(kept.when);
    if (!reviewing && kept.noStatus !== undefined) setNoStatus(kept.noStatus);
    setKept(null);
  };
  // Clear all always clears (F11); ✕ on the arrival's own chip goes back to your filters.
  const setFilters = (next: FilterState) => {
    if (kept && !Object.keys(next).length) {
      setKept(null);
      return setSavedFilters({});
    }
    const arrived = kept ? filters.came_from?.on ?? [] : [];
    if (kept && arrived.some((id) => !(next.came_from?.on ?? []).includes(id))) return backToFilters();
    setSavedFilters(next);
  };
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [shown, setShown] = useState(PAGE);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [details, setDetails] = useState<Map<string, Details>>(new Map());
  const [reference, setReference] = useState<ShelfBook[] | null>(null);
  const [extra, setExtra] = useState<{ byClaude: Set<string>; batches: Batch[] }>({ byClaude: new Set(), batches: [] });
  const [facts, setFacts] = useState<{ facts: FilterFacts[]; lists: FilterLists } | null>(null);
  const [bookSheet, setBookSheet] = useState<{ row: QueueRow; what: BookSheetKind } | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const reloadSuggestions = () =>
    loadSuggestions()
      .then((s) => setSuggestions(s))
      .catch((e: unknown) => {
        setSuggestions([]);
        notice({ message: `Couldn’t read your suggestions: ${e instanceof Error ? e.message : String(e)}`, done: false });
      });
  // The nightly fill-in's questions (step 2) and your answers to them.
  const [lookups, setLookups] = useState<LookupRow[] | null>(null);
  const [dismissals, setDismissals] = useState<Dismissal[]>([]);
  const reloadLookups = () =>
    Promise.all([loadLookups(), loadDismissals()])
      .then(([r, d]) => {
        setLookups(r);
        setDismissals(d);
      })
      .catch((e: unknown) => {
        setLookups([]);
        notice({ message: `Couldn’t read the lookups: ${e instanceof Error ? e.message : String(e)}`, done: false });
      });
  useEffect(() => {
    void reloadSuggestions();
    void reloadLookups();
  }, []);

  // Reference books too (this tab spans every shelf), how each book came, and the filters' facts.
  useEffect(() => {
    let live = true;
    void loadReferenceHeads()
      .then((r) => {
        if (!live) return;
        setReference(r);
        void loadFilterFacts(shelves, todayIn(), r).then((f) => live && setFacts(f)).catch(() => null);
      })
      .catch(() => live && setReference([]));
    void loadRecentFacts()
      .then((x) => {
        if (!live) return;
        setExtra(x);
      })
      .catch(() => null);
    return () => {
      live = false;
    };
  }, [shelves]);

  const days = facts?.lists.recentDays ?? 30;
  const from = recentFrom(today, days);
  const factsById = useMemo(() => new Map(facts?.facts.map((f) => [f.id, f]) ?? []), [facts]);
  const books = useMemo(() => everyBook([...Object.values(shelves), reference ?? []]), [shelves, reference]);
  const typedLists = useMemo(() => new Set(extra.batches.filter((b) => b.kind === 'typed_list').map((b) => b.id)), [extra]);
  const items = useMemo(() => lookupItems(lookups ?? [], books, dismissals), [lookups, books, dismissals]);
  // Update details › No shelf yet: every book with no shelf yet, whatever its date.
  const view = reviewing ? { when: 'any' as When, noStatus: true } : { when, noStatus };
  const inView = useMemo(() => rowsOf(recentRows(books, view, from), factsById), [books, when, noStatus, from, factsById, reviewing]);
  // From Claude only for the admin (Kristi, Oct 31); for anyone else Claude's adds are By hand.
  const claudeSeen = admin ? extra.byClaude : new Set<string>();
  const how = (r: QueueRow) => howAdded(r, claudeSeen);
  // Came from is applied here; the other rows through the shared filters.
  const came = filters.came_from;
  const otherFilters = useMemo(() => {
    const o = { ...filters };
    delete o.came_from;
    return o;
  }, [filters]);
  const cameOk = (b: QueueRow) => {
    if (!came?.on.length) return true;
    const hit = came.on.includes(cameFromOf(b, extra.byClaude, admin));
    return came.mode === 'show' ? hit : !hit;
  };
  const rows = useMemo(() => {
    const on = filterCount(otherFilters) > 0;
    const kept = inView.filter((b) => cameOk(b) && (!on || (factsById.get(b.id) && matches(factsById.get(b.id)!, otherFilters, from))));
    return sortRecent(kept, sort, { how, copy: copyText });
  }, [inView, filters, sort, extra, factsById, from, admin]);
  useEffect(() => onCount?.(rows.length), [rows.length]);
  // No shelf yet counts every book with no shelf yet, whatever else is set, so it matches the dot.
  const noStatusCount = recentRows(books, { when, noStatus: true }, from).length;
  // Within the other filters (F10): what turning the chip on would show. No shelf yet ignores When
  // (it shows every shelfless book), so its count does too, whether it's on or off.
  const noStatusShown = useMemo(() => {
    const on = filterCount(otherFilters) > 0;
    return rowsOf(recentRows(books, { when, noStatus: true }, from), factsById).filter((b) => cameOk(b) && (!on || (factsById.get(b.id) && matches(factsById.get(b.id)!, otherFilters, from)))).length;
  }, [books, when, otherFilters, came, factsById, from, extra, admin]);
  // Each row counts within what the other rows leave (filters review F10, Kristi Nov 2): the shared
  // rows within Came from, and Came from within the shared rows.
  const tabFacts = useMemo(() => {
    const set = new Set(inView.filter(cameOk).map((b) => b.id));
    return facts?.facts.filter((f) => set.has(f.id)) ?? [];
  }, [facts, inView, came, extra, admin]);
  const cameGroup: Group = useMemo(() => {
    const on = filterCount(otherFilters) > 0;
    const pool = on ? inView.filter((b) => factsById.get(b.id) && matches(factsById.get(b.id)!, otherFilters, from)) : inView;
    const chips = cameFromChips(pool, extra.batches, extra.byClaude, admin, came?.on ?? [], today);
    return {
      key: 'came_from',
      row: 'Came from',
      label: 'Came from',
      hideable: true,
      section: 'when',
      chips,
      ...(chips.shown !== undefined ? { firstShown: chips.shown, moreLabel: (n: number) => `+ ${num(n)} older` } : {}),
    };
  }, [inView, extra, admin, came, otherFilters, factsById, from]);
  // The catalog's order (F6): Copies · Format · Shelf, then Came from.
  const groups = useMemo(
    () => {
      // A panel's row order follows the view's job (Kristi, Nov 3): Recently added is about how books
      // came in, so Came from leads; Update details' No shelf yet keeps the catalog's order (Came from last).
      if (!facts) return [cameGroup];
      const rows = buildGroups(tabFacts, facts.lists, otherFilters, from).filter((g) => rowKeys.includes(g.key));
      return reviewing ? [...rows, cameGroup] : [cameGroup, ...rows];
    },
    [facts, tabFacts, otherFilters, from, cameGroup, reviewing],
  );
  const narrowed = filterCount(filters) > 0;

  const visible = rows.slice(0, shown);
  useEffect(() => {
    const missing = visible.filter((b) => !details.has(b.id));
    if (!missing.length) return;
    let live = true;
    loadDetails(missing, new Set())
      .then((m) => live && setDetails((old) => new Map([...old, ...m])))
      .catch(() => null);
    return () => {
      live = false;
    };
  }, [visible.map((b) => b.id).join(',')]);

  // A paper book you no longer have (check-up, Kristi Nov 2): its Copy is a value pill, "Paper ▾",
  // marking the paper copy lost or donated (the copy's own state; Undo in the notice).
  const paperMenu = (b: QueueRow, copy: string | null) =>
    b.formats.includes('paper') && copy ? (
      <PillMenu<'have' | 'lost' | 'donated'>
        label={copy}
        ariaLabel={`Copy of ${b.title}`}
        sheetTitle={`Copy of ${b.title}`}
        choices={[
          { value: 'have', label: copy },
          { value: 'lost', label: 'Paper · lost' },
          { value: 'donated', label: 'Paper · donated' },
        ]}
        current="have"
        onPick={(v) => {
          if (v !== 'have') void setBooksPaperState(notice, [{ id: b.id, title: b.title }], v);
        }}
      />
    ) : null;
  const open = (b: QueueRow) => () => navigate(`/book/${bookLabel(b.label)}`, { state: { from: path }, keepScroll: wide });
  const sortBy = (k: RecentSortKey) => {
    setSort(flipSort(sort, k));
    setShown(PAGE);
  };
  const isSel = (id: string) => selected.includes(id);
  const toggleSel = (id: string) => setSelected(isSel(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const shownIds = visible.map((b) => b.id);
  // From 700 to 1,000 px ★ and Copy fold under the author.
  const fold = wide && narrowInterest;
  const cols: Col<RecentSortKey>[] = [
    ...(selecting ? [{ label: <SelectShown shownIds={shownIds} selected={selected} setSelected={setSelected} />, width: '26px' }] : []),
    { label: 'Added', width: fold ? '72px' : '76px', sort: 'added' as RecentSortKey },
    { label: 'Book', width: fold ? 'minmax(0, 1.6fr)' : 'minmax(0, 1.5fr)', sort: 'title' as RecentSortKey, book: true },
    { label: 'How', width: fold ? '80px' : '86px', sort: 'how' as RecentSortKey, title: 'How added' },
    { label: 'Shelf', width: '132px', sort: 'status' as RecentSortKey },
    { label: 'Read type', width: '128px', sort: 'readtype' as RecentSortKey },
    { label: 'Interest', width: 'var(--q-interest-w)', sort: 'interest' as RecentSortKey },
    ...(fold
      ? []
      : [
          { label: '★', width: '56px', sort: 'goodreads' as RecentSortKey, num: true, title: 'Goodreads' },
          { label: 'Copy', width: '84px', sort: 'copy' as RecentSortKey },
        ]),
    { width: '84px' },
  ];
  const setType = (b: QueueRow, name: string | null) => void setBook(notice, b, 'read_type', name, name ? `${b.title}: ${name}` : `Read type cleared for ${b.title}`);
  const setInterest = (b: QueueRow, v: number | null) => void setBook(notice, b, 'interest', v, v === null ? `Interest cleared for ${b.title}` : `Interest set to ${interestHint(v)}`);
  const restart = () => {
    setShown(PAGE);
    setSelected([]);
  };

  // No shelf yet: worth a look (filters review, Kristi Nov 2): the ring; its to-do home is Update
  // details' first list. In Add & import it sits before Filters on the one line, as on the board.
  const shelfChip = reviewing ? null : (
    <ChipRow label="Quick filters">
      <FilterChip
        label="No shelf yet"
        count={noStatusShown}
        on={noStatus}
        worth={noStatusShown ? `Worth a look: ${num(noStatusShown)} with no shelf yet` : undefined}
        onToggle={() => {
          setNoStatus(!noStatus);
          restart();
        }}
      />
    </ChipRow>
  );
  // "narrowed to Sheet import, run 3" (Kristi, Oct 31: the total isn't hidden, the narrowing is said).
  const narrowedTo = came?.on.length ? (came.mode === 'hide' ? 'all but ' : '') + came.on.map((id) => cameGroup.chips.find((c) => c.id === id)?.label ?? id).join(', ') : null;
  const tools = (
    <>
      {shelfChip}
      <span class="shelves-divider" aria-hidden="true" />
      <FilterBar
        groups={groups}
        state={filters}
        open={filtersOpen}
        onToggle={() => setFiltersOpen(!filtersOpen)}
        onChange={(s) => {
          setFilters(facts ? pruneSubgenres(s, facts.lists) : s);
          setShown(PAGE);
        }}
        showing={narrowed ? rows.length : null}
      />
      <span class="queue-top-end">
        {/* No Sort ▾ on a laptop or iPad, in Add & import and Update details alike (Kristi, Nov 3): the
            column headers sort, and it's less cluttered. A phone has no header row, so it always keeps
            the Sort pill (R6 T8). */}
        {!wide ? (
          <SettingMenu<RecentSortKey>
            name="Sort"
            choices={RECENT_SORTS}
            value={sort.key}
            onChange={(k) => {
              setSort({ key: k, reversed: false });
              setShown(PAGE);
            }}
          />
        ) : null}
        <button
          type="button"
          class={`btn-secondary queue-select${selecting ? ' on' : ''}`}
          aria-pressed={selecting}
          aria-label="Select several"
          onClick={() => {
            setSelecting(!selecting);
            setSelected([]);
          }}
        >
          {wide ? 'Select several' : 'Select'}
        </button>
        {top}
      </span>
    </>
  );
  const panel = filtersOpen ? (
        facts ? (
          <FilterPanel groups={groups} state={filters} onChange={(s) => setFilters(pruneSubgenres(s, facts.lists))} onClose={() => setFiltersOpen(false)} showing={rows.length} />
        ) : (
          <p class="shelves-quiet">Loading the filters…</p>
        )
      ) : null;
  // "← Back to your filters" after arriving from a link (F11): top left, where back links go (R8).
  const back = kept && !hideBack ? <BackLink where="your filters" onClick={backToFilters} /> : null;
  // When stays the view switch (R3 P3; Kristi, Nov 3), at the right of the section's heading when
  // Add & import gives it a slot.
  const whenMenu = reviewing ? null : (
    <span class="ra-showing">
      <span>When</span>
      <Segmented
        label="When"
        options={[
          { key: 'recent' as When, name: `Last ${days} days` },
          { key: 'any' as When, name: 'Any date' },
        ]}
        value={when}
        onChange={(k) => {
          setWhen(k);
          restart();
        }}
      />
    </span>
  );
  const controls = (
    <>
      <div class="rv-tools">{tools}</div>
      {whenMenu && !headSlot ? <div class="ra-when">{whenMenu}</div> : null}
      {whenMenu && headSlot ? createPortal(whenMenu, headSlot) : null}
    </>
  );
  const body = (
    <>
      {back}
      {controls}
      {panel}
      {/* What's showing, in words (R7), with room above and below (Kristi, Nov 3). */}
      {reviewing ? null : <p class="ra-line">{recentLine(view, days, narrowedTo, rows.length)}</p>}
      {reference === null ? (
        <p class="shelves-quiet">Loading…</p>
      ) : (
        <ListTable<RecentSortKey>
          label="Recently added"
          class={`ra-list${selecting ? ' selecting' : ''}`}
          cols={cols}
          sort={{ key: sort.key, desc: sort.reversed }}
          onSort={sortBy}
          empty={inView.length ? 'Nothing here matches.' : view.noStatus ? 'Every book is on a shelf.' : when === 'recent' ? `Nothing added in the last ${days} days.` : 'No books yet.'}
          onClear={inView.length ? () => setFilters({}) : undefined}
          foot={<ListFoot shown={Math.min(shown, rows.length)} total={rows.length} page={PAGE} onMore={() => setShown(shown + PAGE)} onAll={() => setShown(rows.length)} onFewer={() => setShown(PAGE)} />}
        >
          {visible.map((raw) => {
            const b = { ...raw, ...details.get(raw.id), owned: raw.owned } as QueueRow;
            const copy = copyText(b);
            const gr = b.goodreads ? `★ ${b.goodreads.toFixed(2)}` : null;
            const added = addedText(b.addedAt, today);
            const check = selecting ? <RowCheck title={b.title} on={isSel(b.id)} onToggle={() => toggleSel(b.id)} /> : null;
            // Up to 1,000 px: ★ and Copy under the author; on a phone also when and how it was added.
            const meta = fold || !wide ? [wide ? null : `Added ${added} · ${how(b)}`, gr, copy ?? 'Not owned yet'].filter(Boolean).join(' · ') : null;
            const book = <RowBook title={b.title} sub={[b.author, b.series].filter(Boolean).join(' · ')} cover={b.cover} meta={meta} onOpen={open(b)} />;
            const shelf = (
              <span class="ra-status">
                {/* No shelf yet is worth a look (Kristi, Nov 2): a dashed value set in place, opening Move to. */}
                {b.status === null ? (
                  <MoveMenu row={b} pastRead={Boolean(editOn)} onSheet={(what) => setBookSheet({ row: b, what })} asValue="No shelf yet" />
                ) : (
                  <span class="pill">{statusText(b)}</span>
                )}
              </span>
            );
            const type = (
              <span class="q-type">
                <TypeMenu row={b} names={readTypes} onPick={(n) => setType(b, n)} />
              </span>
            );
            const move = (
              <span class="rv-actions">
                <MoveMenu row={b} pastRead={Boolean(editOn)} onSheet={(what) => setBookSheet({ row: b, what })} />
              </span>
            );
            return (
              <ListRow
                key={b.id}
                data-row={b.id}
                class="ra-row"
                selected={isSel(b.id)}
                cells={[
                  ...(selecting ? [check] : []),
                  <span class="ra-added">{added}</span>,
                  book,
                  <span class="ra-how">{how(b)}</span>,
                  shelf,
                  type,
                  <span class="q-interest">
                    {narrowInterest ? <InterestMenu row={b} onPick={(v) => setInterest(b, v)} /> : <InterestDots value={b.interest} hints={INTEREST_HINTS} onChange={(v) => setInterest(b, v)} />}
                  </span>,
                  ...(fold
                    ? []
                    : [
                        <span class="q-gr" title="Goodreads">
                          {gr ?? '–'}
                        </span>,
                        <span class="q-copy">{paperMenu(b, copy) ?? (copy ? copy : <span class="tile-notowned">Not owned yet</span>)}</span>,
                      ]),
                  // Up to 1,000 px Copy folds under the title, so a paper book's Copy ▾ sits beside Move.
                  fold && paperMenu(b, copy) ? <span class="ra-move-pair">{paperMenu(b, copy)}{move}</span> : move,
                ]}
                phone={{
                  lead: check,
                  book,
                  control: move,
                  line: (
                    <span class="q-controls">
                      {shelf}
                      {type}
                      {paperMenu(b, copy)}
                      <span class="q-interest">
                        <InterestMenu row={b} onPick={(v) => setInterest(b, v)} />
                      </span>
                    </span>
                  ),
                }}
              />
            );
          })}
        </ListTable>
      )}
      {selecting ? (
        <BulkBar
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          readTypes={readTypes}
          onPaperState={(books, state) => {
            setSelected([]);
            void setBooksPaperState(notice, books, state);
          }}
        />
      ) : null}
      {bookSheet ? <BookSheet label={bookSheet.row.label} title={bookSheet.row.title} what={bookSheet.what} onClose={() => setBookSheet(null)} /> : null}
    </>
  );
  if (!reviewing) return body;
  return (
    <>
      <div class="tab-head">
        <p class="queue-description">{myBooksTab('review').description}</p>
      </div>
      {reference === null ? (
        <p class="shelves-quiet">Loading…</p>
      ) : (
        <ReviewView
          books={books}
          typedLists={typedLists}
          readTypes={readTypes}
          suggestions={suggestions}
          setSuggestions={setSuggestions}
          reload={reloadSuggestions}
          items={lookups === null ? null : items}
          dismissals={dismissals}
          setDismissals={setDismissals}
          reloadLookups={reloadLookups}
          noShelf={{ count: noStatusCount, render: () => body }}
        />
      )}
    </>
  );
}
